import { choiceNames, lineAmount, type Perm } from '@sj/shared';
import { Fragment, isValidElement, type MouseEvent, type ReactNode } from 'react';
import { useSession } from '../../app/session.js';
import { useViewOrFallback } from '../../app/view.js';
import { BankSelect } from '../../components/Choice.js';
import { Icon } from '../../components/Icon.js';
import { ItemsTable } from '../../components/ItemsTable.js';
import { type ModalHandle, useConfirm, useModal } from '../../components/Modal.js';
import { PhotoInput } from '../../components/PhotoInput.js';
import { useToast } from '../../components/Toast.js';
import { Pill } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { getPosition, telLink } from '../../lib/device.js';
import { notaDocHtml } from '../../lib/doc.js';
import { printHtml } from '../../lib/export.js';
import { fv } from '../../lib/form.js';
import { addDays, fmtDay, fmtDT, num, rp, today } from '../../lib/format.js';
import { label } from '../../lib/status.js';
import { DeliverBody, newDeliverState } from './DeliverBody.js';
import type { Nota, NotaDetail, NotaItem } from './types.js';

// Nota & aksi-aksinya — port openNota / editNota / loadModal / payModal / A.deliverModal / A.failModal v1 (ops.js).
// Semua dalam satu hook agar bisa saling memanggil (mis. simpan pembayaran → buka detail nota lagi).

export function useNotaOps() {
  const { open } = useModal();
  const confirm = useConfirm();
  const toast = useToast();
  const { me, meta, isMgr, can, refreshCounts } = useSession();
  // v1 A.render(): muat ulang halaman aktif (juga bila dibuka dari luar halaman — lihat useViewOrFallback()).
  const { reload: render } = useViewOrFallback();

  async function openNota(id: number): Promise<void> {
    const d = await api<NotaDetail>('GET', `/api/notas/${id}`);
    const n = d.item;
    const mgr = isMgr;
    const drv = me.role === 'driver';
    const delivered = ['terkirim', 'selesai'].includes(n.status);
    const remaining = n.total - n.paid - d.pending;
    const qtyKey = delivered ? 'qty_received' : 'qty';

    const acts = (mm: ModalHandle) => {
      // Aksi dengan konfirmasi: konfirmasi dulu, lalu jalankan dengan tombol dinonaktifkan.
      const withBtn = (fn: (btn: HTMLButtonElement) => Promise<void>) => (e: MouseEvent<HTMLButtonElement>) =>
        void fn(e.currentTarget);
      const out: ReactNode[] = [];
      if (mgr && n.status === 'disiapkan')
        out.push(
          <button
            key="cancel"
            type="button"
            className="btn btn-danger"
            data-m="cancel"
            onClick={withBtn(async (btn) => {
              const reason = await confirm('Batalkan nota', 'Nota dibatalkan (belum ada barang keluar).', {
                danger: true,
                ok: 'Batalkan',
                input: 'Alasan',
              });
              if (reason)
                await mm.run(async () => {
                  await api('POST', `/api/notas/${n.id}/cancel`, { reason });
                  mm.close();
                  toast('Nota dibatalkan.');
                  render();
                }, btn);
            })}
          >
            Batalkan
          </button>,
          <button
            key="edit"
            type="button"
            className="btn"
            data-m="edit"
            onClick={() => {
              mm.close();
              editNota(n);
            }}
          >
            Ubah
          </button>,
          <button
            key="load"
            type="button"
            className="btn btn-primary"
            data-m="load"
            onClick={() => {
              mm.close();
              loadModal(n, d.items);
            }}
          >
            <Icon n="truck" cls="i-sm" /> Verifikasi Muat
          </button>,
        );
      if (mgr && n.status === 'dimuat')
        out.push(
          <button
            key="edit"
            type="button"
            className="btn"
            data-m="edit"
            onClick={() => {
              mm.close();
              editNota(n);
            }}
          >
            Ganti driver
          </button>,
        );
      if ((mgr || drv) && ['dimuat', 'dikirim'].includes(n.status))
        out.push(
          <button
            key="fail"
            type="button"
            className="btn btn-danger"
            data-m="fail"
            onClick={() => {
              mm.close();
              failWith(n);
            }}
          >
            Gagal kirim
          </button>,
          <button
            key="deliver"
            type="button"
            className="btn btn-ok"
            data-m="deliver"
            onClick={() => {
              mm.close();
              deliverWith(n, d.items);
            }}
          >
            <Icon n="check" cls="i-sm" /> Terkirim
          </button>,
        );
      if (mgr && n.status === 'terkirim')
        out.push(
          <button
            key="ret"
            type="button"
            className="btn btn-primary"
            data-m="ret"
            onClick={withBtn(async (btn) => {
              const back = d.items.filter((i) => (i.qty_received ?? 0) < i.qty);
              const ok = await confirm(
                'Nota kembali',
                <>
                  Pastikan nota fisik/foto &amp; barang sisa sudah dicek.
                  <br />
                  <br />
                  {back.length ? (
                    <>
                      <b>Retur masuk stok:</b>
                      {back.map((i) => (
                        <Fragment key={i.id}>
                          <br />
                          {`${i.name}: ${num(i.qty - (i.qty_received ?? 0))} ${i.unit}`}
                        </Fragment>
                      ))}
                    </>
                  ) : (
                    'Tidak ada sisa barang.'
                  )}
                </>,
                { ok: 'Nota sudah kembali' },
              );
              if (ok)
                await mm.run(async () => {
                  await api('POST', `/api/notas/${n.id}/return`, {});
                  mm.close();
                  toast('Nota selesai.');
                  render();
                  void refreshCounts();
                }, btn);
            })}
          >
            <Icon n="receipt" cls="i-sm" /> Nota Kembali
          </button>,
        );
      if (mgr && n.status === 'gagal')
        out.push(
          <button
            key="resolveCancel"
            type="button"
            className="btn btn-danger"
            data-m="resolveCancel"
            onClick={withBtn(async (btn) => {
              if (await confirm('Batalkan nota', 'Barang dikembalikan ke stok dan nota dibatalkan.', { danger: true }))
                await mm.run(async () => {
                  await api('POST', `/api/notas/${n.id}/resolve`, { action: 'batal' });
                  mm.close();
                  toast('Nota dibatalkan.');
                  render();
                }, btn);
            })}
          >
            Batalkan nota
          </button>,
          <button
            key="resolveRetry"
            type="button"
            className="btn btn-primary"
            data-m="resolveRetry"
            onClick={withBtn(async (btn) => {
              if (await confirm('Jadwalkan ulang', 'Barang dikembalikan ke stok, nota kembali ke "Perlu dimuat" untuk besok.'))
                await mm.run(async () => {
                  await api('POST', `/api/notas/${n.id}/resolve`, { action: 'ulang', ship_date: addDays(today(), 1) });
                  mm.close();
                  toast('Nota dijadwalkan ulang.');
                  render();
                }, btn);
            })}
          >
            Jadwalkan ulang
          </button>,
        );
      // Sebelum dikirim: titipan (toko bayar sebagian/di muka) — mengurangi tagihan yang dibawa driver.
      const beforeDelivery = ['disiapkan', 'dimuat', 'dikirim'].includes(n.status);
      if (mgr && (delivered || beforeDelivery) && remaining > 0)
        out.push(
          <button
            key="pay"
            type="button"
            className="btn btn-soft"
            data-m="pay"
            onClick={() => {
              mm.close();
              payModal(n, remaining);
            }}
          >
            <Icon n="wallet" cls="i-sm" /> {beforeDelivery ? 'Catat titipan' : 'Catat pembayaran'}
          </button>,
        );
      // Tiap aksi hanya untuk peran yang punya haknya (peran kustom).
      const NEED: Record<string, Perm> = {
        cancel: 'nota.ubah',
        edit: 'nota.ubah',
        load: 'nota.muat',
        fail: 'nota.kirim',
        deliver: 'nota.kirim',
        ret: 'nota.kembali',
        resolveCancel: 'nota.kembali',
        resolveRetry: 'nota.kembali',
        pay: 'keuangan.bayar',
      };
      return out.filter((x) => {
        const k = isValidElement(x) ? String(x.key) : '';
        return !NEED[k] || can(NEED[k] as Perm);
      });
    };

    open({
      wide: true,
      title: (
        <>
          Nota <span className="mono">{n.no}</span> <Pill kind="nota" v={n.status} />
        </>
      ),
      body: (
        <>
          <div className="grid-2">
            <dl className="kv">
              <dt>Toko</dt>
              <dd>
                {n.store_name}
                <div className="muted small">{n.address}</div>
              </dd>
              <dt>Telepon</dt>
              <dd>{n.store_phone ? <a href={telLink(n.store_phone)}>{n.store_phone}</a> : '-'}</dd>
              <dt>Sales / SPO</dt>
              <dd>
                {`${n.sales_name} · `}
                <span className="mono">{n.spo_no}</span>
              </dd>
              <dt>Tgl kirim</dt>
              <dd>{fmtDay(n.ship_date)}</dd>
              <dt>Driver</dt>
              <dd>{n.driver_name || '—'}</dd>
            </dl>
            <dl className="kv">
              <dt>Metode bayar</dt>
              <dd>
                <Pill kind="pm" v={n.pay_method} /> {delivered ? <Pill kind="pay" v={n.pay_status} /> : null}
              </dd>
              {n.due_date && delivered ? (
                <>
                  <dt>Jatuh tempo</dt>
                  <dd>{fmtDay(n.due_date)}</dd>
                </>
              ) : null}
              {n.loaded_at ? (
                <>
                  <dt>Dimuat</dt>
                  <dd>{`${fmtDT(n.loaded_at)} · ${n.loaded_by_name || ''}`}</dd>
                  <dt>Cek muat driver</dt>
                  <dd>
                    {n.drv_loaded_at ? (
                      <span className="ok-txt">{`✓ ${fmtDT(n.drv_loaded_at)}`}</span>
                    ) : (
                      <span className="warn-txt">belum dicek</span>
                    )}
                    {n.load_issue && !n.drv_loaded_at ? (
                      <div className="small danger-txt">{`Selisih: ${n.load_issue}`}</div>
                    ) : null}
                  </dd>
                </>
              ) : null}
              {n.short_note ? (
                <>
                  <dt>Kurang kirim</dt>
                  <dd className="warn-txt">{n.short_note}</dd>
                </>
              ) : null}
              {n.delivered_at ? (
                <>
                  <dt>Diterima</dt>
                  <dd>{`${fmtDT(n.delivered_at)} · ${n.receiver_name}`}</dd>
                </>
              ) : null}
              {n.returned_at ? (
                <>
                  <dt>Nota kembali</dt>
                  <dd>{`${fmtDT(n.returned_at)} · ${n.returned_by_name || ''}`}</dd>
                </>
              ) : null}
              {n.fail_reason ? (
                <>
                  <dt>Alasan</dt>
                  <dd className="danger-txt">{n.fail_reason}</dd>
                </>
              ) : null}
              {n.note ? (
                <>
                  <dt>Catatan</dt>
                  <dd>{n.note}</dd>
                </>
              ) : null}
            </dl>
          </div>
          <ItemsTable items={d.items} qtyKey={qtyKey} cost={can('laporan.hpp')} />
          {delivered ? (
            <div className="grid3">
              <div className="kpi">
                <span className="lbl">Tagihan</span>
                <span className="val">{rp(n.total)}</span>
              </div>
              <div className="kpi">
                <span className="lbl">Terbayar</span>
                <span className="val ok-txt">{rp(n.paid)}</span>
              </div>
              <div className="kpi">
                <span className="lbl">Sisa</span>
                <span className={`val ${remaining > 0 ? 'danger-txt' : ''}`}>{rp(Math.max(0, n.total - n.paid))}</span>
                {d.pending ? <span className="sub">{`${rp(d.pending)} transfer menunggu konfirmasi`}</span> : null}
              </div>
            </div>
          ) : null}
          {n.proof_file ? (
            <div>
              <div className="small muted b">Foto nota / serah terima</div>
              <a href={`/api/files/${n.proof_file}`} target="_blank" rel="noopener">
                <img className="thumb" src={`/api/files/${n.proof_file}`} alt="Bukti" />
              </a>
            </div>
          ) : null}
          {d.payments.length ? (
            <div className="stack-sm">
              <b>Pembayaran</b>
              {d.payments.map((p) => (
                <div className="line" key={p.id}>
                  <div className="body">
                    <b>{`${rp(p.amount)} · ${label('pm', p.method)}`}</b>
                    <span>
                      {`${fmtDay(p.date)}${p.collected_by_name ? ` · dipegang ${p.collected_by_name}` : ''}${p.deposit_no ? ` · disetor ${p.deposit_no}` : ''}${p.ref ? ` · ref ${p.ref}` : ''}${p.note ? ` · ${p.note}` : ''}`}
                    </span>
                  </div>
                  {p.proof_file ? (
                    <a href={`/api/files/${p.proof_file}`} target="_blank" rel="noopener" className="btn btn-sm">
                      Bukti
                    </a>
                  ) : null}
                  <Pill kind="payment" v={p.status} />
                </div>
              ))}
            </div>
          ) : null}
        </>
      ),
      foot: (mm) => (
        <>
          <button
            type="button"
            className="btn"
            data-m="print"
            onClick={() => printHtml(notaDocHtml(meta, { ...n, store_address: n.address }, d.items, qtyKey))}
          >
            <Icon n="print" cls="i-sm" /> Cetak
          </button>
          {acts(mm)}
        </>
      ),
    });
  }

  function editNota(n: Nota) {
    const drivers = meta.drivers ?? [];
    const disiapkan = n.status === 'disiapkan';
    open({
      title: (
        <>
          Ubah nota <span className="mono">{n.no}</span>
        </>
      ),
      body: (
        <>
          <label className="f">
            Driver
            <select className="inp" name="driver_id" defaultValue={n.driver_id == null ? '' : String(n.driver_id)}>
              {disiapkan ? <option value="">(belum)</option> : null}
              {drivers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
          {disiapkan ? (
            <>
              <label className="f">
                Tanggal kirim
                <input autoComplete="off" className="inp" type="date" name="ship_date" defaultValue={n.ship_date} />
              </label>
              <label className="f">
                Metode bayar
                <select className="inp" name="pay_method" defaultValue={n.pay_method}>
                  {['cash', 'transfer', 'tempo'].map((k) => (
                    <option key={k} value={k}>
                      {label('pm', k)}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <label className="f">
            Catatan
            <input autoComplete="off" className="inp" name="note" defaultValue={n.note} />
          </label>
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            Simpan
          </button>
        </>
      ),
      submit: async (f, m) => {
        const body: Record<string, unknown> = { driver_id: fv(f, 'driver_id') || null, note: fv(f, 'note') };
        if (f.elements.namedItem('ship_date')) {
          body.ship_date = fv(f, 'ship_date');
          body.pay_method = fv(f, 'pay_method');
        }
        await api('PUT', `/api/notas/${n.id}`, body);
        m.close();
        toast('Nota diperbarui.');
        render();
        await openNota(n.id);
      },
    });
  }

  function loadModal(n: Nota, items: NotaItem[]) {
    const drivers = meta.drivers ?? [];
    open({
      wide: true,
      title: (
        <>
          Verifikasi Muat <span className="mono">{n.no}</span>
        </>
      ),
      body: (
        <>
          <div className="note">
            Hitung barang fisik yang dinaikkan ke kendaraan. Centang tiap baris bila qty <b>sama</b> dengan nota. Bila berbeda,
            batalkan &amp; koreksi lewat nota baru.
          </div>
          <dl className="kv">
            <dt>Toko</dt>
            <dd>{`${n.store_name} · ${n.area || ''}`}</dd>
            <dt>Bayar</dt>
            <dd>{label('pm', n.pay_method)}</dd>
          </dl>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>✓</th>
                  <th>Produk</th>
                  <th className="right">Qty muat</th>
                  <th className="right">Stok gudang</th>
                  <th className="right">Harga</th>
                  <th className="right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <input type="checkbox" className="check-in" name="chk" value={i.id} />
                    </td>
                    <td>{i.name}</td>
                    <td className="right b">{`${num(i.qty)} ${i.unit}`}</td>
                    <td
                      className={`right ${(i.stock ?? 0) + 1e-9 < i.qty * (i.factor || 1) ? 'danger-txt b' : 'muted'}`}
                    >{`${num(i.stock)} ${i.base_unit || ''}`}</td>
                    <td className="right">
                      {rp(i.price)}
                      {i.disc ? <div className="small ok-txt">{`−${rp(i.disc)}`}</div> : null}
                    </td>
                    <td className="right">{rp(lineAmount(i.qty, i.price, i.disc))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>Total ditagihkan ke client</td>
                  <td className="right">{rp(n.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <label className="check">
            <input
              type="checkbox"
              id="chkAll"
              onChange={(e) => {
                const on = e.currentTarget.checked;
                for (const c of e.currentTarget.form?.querySelectorAll<HTMLInputElement>('[name=chk]') ?? []) c.checked = on;
              }}
            />
            {' Centang semua'}
          </label>
          <label className="f">
            Driver yang membawa
            <select className="inp" name="driver_id" required defaultValue={n.driver_id == null ? '' : String(n.driver_id)}>
              <option value="">— pilih driver —</option>
              {drivers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            <Icon n="truck" cls="i-sm" /> Sah — Barang Dimuat
          </button>
        </>
      ),
      submit: async (f, m) => {
        if (!fv(f, 'driver_id')) throw new Error('Pilih driver.');
        const checked = [...f.querySelectorAll<HTMLInputElement>('[name=chk]')]
          .filter((c) => c.checked)
          .map((c) => Number(c.value));
        await api('POST', `/api/notas/${n.id}/load`, { driver_id: Number(fv(f, 'driver_id')), checked });
        m.close();
        toast('Barang dimuat — stok keluar tercatat.');
        render();
        void refreshCounts();
      },
    });
  }

  function payModal(n: Nota, remaining: number) {
    const photos = { current: {} as Record<string, string> };
    open({
      title: (
        <>
          Catat pembayaran <span className="mono">{n.no}</span>
        </>
      ),
      body: (
        <>
          <div className="total-box">
            <span>Sisa tagihan</span>
            <span>{rp(remaining)}</span>
          </div>
          <div className="grid2">
            <label className="f">
              Nominal
              <input
                autoComplete="off"
                className="inp amt"
                type="number"
                name="amount"
                min="1"
                max={remaining}
                defaultValue={remaining}
                required
              />
            </label>
            <label className="f">
              Tanggal
              <input autoComplete="off" className="inp" type="date" name="date" defaultValue={today()} />
            </label>
          </div>
          <label className="f">
            Metode
            <select className="inp" name="method">
              <option value="transfer">Transfer (sudah dicek di mutasi)</option>
              <option value="cash">Cash</option>
            </select>
          </label>
          <label className="f">
            {'Cash ditagih oleh driver? '}
            <span className="hint">Pilih driver bila uang masih dipegang driver (masuk setoran driver).</span>
            <select className="inp" name="collected_by">
              <option value="">Tidak — diterima langsung di kantor</option>
              {(meta.drivers ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
          <BankSelect name="bank_account_id" label="Rekening tujuan (untuk transfer)" />
          <label className="f">
            No. referensi / keterangan
            <input autoComplete="off" className="inp" name="ref" maxLength={80} />
          </label>
          <PhotoInput name="proof" label="Foto bukti (opsional)" photos={photos} />
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            Simpan pembayaran
          </button>
        </>
      ),
      submit: async (f, m) => {
        const method = fv(f, 'method');
        await api('POST', `/api/notas/${n.id}/payments`, {
          amount: Number(fv(f, 'amount')),
          date: fv(f, 'date'),
          method,
          collected_by: method === 'cash' ? fv(f, 'collected_by') || null : null,
          bank_account_id: method === 'transfer' ? fv(f, 'bank_account_id') || null : null,
          ref: fv(f, 'ref'),
          proof_photo: photos.current.proof,
        });
        m.close();
        toast('Pembayaran tercatat.');
        render();
        await openNota(n.id);
      },
    });
  }

  /** Serah terima (v1 A.deliverModal(n, items)). */
  function deliverWith(n: Nota, items: NotaItem[], onDone?: () => void) {
    const photos = { current: {} as Record<string, string> };
    const st = newDeliverState(n, items);
    const drv = me.role === 'driver';
    let pos: { lat: number; lng: number } | null = null;
    void getPosition().then((p) => {
      pos = p;
    });
    open({
      title: (
        <>
          Serah terima <span className="mono">{n.no}</span>
        </>
      ),
      body: <DeliverBody n={n} items={items} st={st} tiers={meta.discount_tiers} drv={drv} photos={photos} />,
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-ok btn-lg" type="submit">
            <Icon n="check" cls="i-sm" /> Simpan — Terkirim
          </button>
        </>
      ),
      submit: async (f, m) => {
        const { got, method } = st;
        const miss = got.filter((g) => !g.ok);
        if (miss.length) throw new Error(`Centang dulu barang yang sudah diturunkan: ${miss.map((g) => g.name).join(', ')}.`);
        const short = got.some((g) => g.rec < g.qty);
        if (short && !fv(f, 'short_note')) throw new Error('Ada barang kurang diturunkan — pilih alasannya.');
        const body: Record<string, unknown> = {
          items: got.map((g) => ({ item_id: g.id, qty_received: g.rec, checked: g.ok })),
          receiver_name: fv(f, 'receiver_name'),
          pay_method: method,
          proof_photo: photos.current.proof,
          lat: pos?.lat,
          lng: pos?.lng,
          short_note: short ? fv(f, 'short_note') : '',
        };
        if (method === 'cash') body.cash_amount = Number(fv(f, 'cash_amount') || 0);
        if (method === 'transfer')
          Object.assign(body, {
            transfer_amount: Number(fv(f, 'transfer_amount') || 0),
            transfer_ref: fv(f, 'transfer_ref'),
            transfer_bank_id: fv(f, 'transfer_bank_id') || null,
            transfer_photo: photos.current.transfer,
          });
        if (method === 'tempo' && f.elements.namedItem('due_date')) body.due_date = fv(f, 'due_date');
        const r = await api<{ total: number }>('POST', `/api/notas/${n.id}/deliver`, body);
        m.close();
        toast(`Terkirim · tagihan ${rp(r.total)}`);
        if (onDone) onDone();
        else render();
      },
    });
  }

  /** Gagal kirim (v1 A.failModal(n)). */
  function failWith(n: Nota, onDone?: () => void) {
    // Alasan dari Data Master → Alasan gagal kirim; keterangan bebas opsional.
    const failReasons = choiceNames(meta.master, 'fail_reason');
    open({
      title: (
        <>
          Gagal kirim <span className="mono">{n.no}</span>
        </>
      ),
      body: (
        <>
          <div className="stack-sm">
            {failReasons.map((r, i) => (
              <label className="check" key={r}>
                <input type="radio" name="r" value={r} defaultChecked={i === 0} />
                {` ${r}`}
              </label>
            ))}
            {failReasons.length ? null : <span className="hint">Tambahkan Alasan gagal kirim di menu Data Master.</span>}
          </div>
          <label className="f">
            Keterangan (opsional)
            <input autoComplete="off" className="inp" name="other" maxLength={200} />
          </label>
          <div className="note">Barang dibawa kembali ke gudang. Admin akan menjadwalkan ulang atau membatalkan.</div>
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-danger" type="submit">
            Simpan gagal kirim
          </button>
        </>
      ),
      submit: async (f, m) => {
        const sel = [...f.querySelectorAll<HTMLInputElement>('[name=r]')].find((x) => x.checked);
        await api('POST', `/api/notas/${n.id}/fail`, { reason: sel ? sel.value : '', note: fv(f, 'other').trim() });
        m.close();
        toast('Gagal kirim dicatat.');
        if (onDone) onDone();
        else render();
      },
    });
  }

  return { openNota, editNota, loadModal, payModal, deliverWith, failWith };
}
